import { reactive } from 'vue'

/**
 * # PDP Breadcrumb Navigation Context (ECOM-2383)
 *
 * Stores the customer's in-app navigation context so a PDP can render the
 * breadcrumb that matches the PLP they came from, instead of the default
 * "master" breadcrumb selected per ECOM-2381.
 *
 * The context is the originating PLP's category id path (root → leaf, i.e. the
 * active category's `rootlineIds`) plus the id of the product that was clicked.
 *
 * ## Why client-only module state
 *
 * The store is a module-level `reactive` value written **only in the browser**
 * (from the PLP product-grid click handler). This gives the exact semantics the
 * ticket requires for free:
 * - It survives SPA navigation within the session.
 * - It is empty during SSR and after a hard reload / Nuxt app restart, so a
 *   reloaded or deeplinked PDP falls back to the ECOM-2381 breadcrumb.
 * - It never serializes into the SSR payload and adds no URL parameters, so it
 *   cannot create multiple canonical URLs for one product.
 *
 * SSR-safety: the server never writes the store, so server-side reads always see
 * the empty default. Mirrors the precedent in `usePpkContext`.
 *
 * ## Why it is keyed by product id
 *
 * Only the PLP product-grid click sets the context, tagged with the clicked
 * product's id. A PDP reads it only when the stored product id matches the PDP's
 * product. This makes most of the ticket's exclusion list work without parsing
 * routes: navigations that do not go through the grid click (product sliders,
 * search, wishlist, basket, recommendations) leave a non-matching id behind, so
 * the next PDP falls back automatically.
 *
 * ## Why the context is aged out after one navigation (ECOM-2383 QA fix)
 *
 * Keying by product id alone is not enough: if the customer clicks product X on
 * a PLP (context set for X) and later re-opens the *same* product X from a
 * non-PLP entry (search, basket, wishlist), the stored id still matches and the
 * stale PLP breadcrumb would wrongly win. The context is only meant to describe
 * the *immediately preceding* PLP→PDP hop, so it is valid for exactly one
 * completed navigation: `setNavigationContext` grants it a one-navigation grace
 * (so it survives the PLP→PDP hop and every consumer read on that PDP), and
 * `ageNavigationContext` — wired to `router.afterEach` in a client plugin —
 * clears it on the next navigation. A later PDP entry via search/basket/wishlist
 * then falls back to the ECOM-2381 breadcrumb.
 */
export interface PdpBreadcrumbContext {
  categoryIds: number[]
  productId: number
}

const state = reactive<{
  context: PdpBreadcrumbContext | null
  /**
   * Completed navigations the current context still survives. Set to 1 on
   * capture so the context outlives the capturing PLP→PDP hop, then decremented
   * to 0 by that hop's `afterEach`; the following navigation clears it.
   */
  navigationsUntilStale: number
}>({
  context: null,
  navigationsUntilStale: 0,
})

export default function usePdpBreadcrumbContext() {
  /**
   * Store the originating PLP category id path for the clicked product. Call from
   * the browser only (PLP product-grid click). Overwrites any prior context.
   */
  const setNavigationContext = (categoryIds: number[], productId: number) => {
    state.context = { categoryIds: [...categoryIds], productId }
    state.navigationsUntilStale = 1
  }

  /**
   * Return the stored PLP category id path when it belongs to the given product,
   * otherwise `null` (no context, or context belongs to another product).
   */
  const getNavigationContextFor = (productId?: number): number[] | null => {
    if (productId === undefined || state.context?.productId !== productId) {
      return null
    }
    return state.context.categoryIds
  }

  const clearNavigationContext = () => {
    state.context = null
    state.navigationsUntilStale = 0
  }

  /**
   * Age the context by one completed navigation. Called from `router.afterEach`
   * (client plugin). The capturing PLP→PDP hop spends the one-navigation grace;
   * the next navigation clears the context so a subsequent PDP entry via
   * search/basket/wishlist falls back to the ECOM-2381 breadcrumb.
   */
  const ageNavigationContext = () => {
    if (!state.context) {
      return
    }
    if (state.navigationsUntilStale > 0) {
      state.navigationsUntilStale -= 1
      return
    }
    clearNavigationContext()
  }

  return {
    setNavigationContext,
    getNavigationContextFor,
    clearNavigationContext,
    ageNavigationContext,
  }
}
